import React, { forwardRef, useState } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon } from 'lucide-react'

// Tipos base do input
type InputSize = 'sm' | 'md' | 'lg'
type InputVariant = 'default' | 'error' | 'success'

// Interface para aparência do input
interface InputAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  placeholderColor?: string
}

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  // Configuração visual
  variant?: InputVariant
  size?: InputSize
  
  // Estados
  error?: boolean
  success?: boolean
  
  // Ícones
  leftIcon?: LucideIcon
  rightIcon?: LucideIcon
  
  // Botão trailing (ex: toggle de senha)
  trailingButton?: {
    icon: LucideIcon
    onClick: () => void
    'aria-label'?: string
  }
  
  // Máscara de entrada
  mask?: string
  maskChar?: string
  unmask?: boolean
  
  // Labels e mensagens
  label?: string
  helperText?: string
  errorMessage?: string
  
  // Customização
  appearance?: InputAppearance
  
  // Layout
  fullWidth?: boolean
}

// Presets de variantes
const variantPresets: Record<InputVariant, InputAppearance> = {
  default: {
    backgroundColor: colors.background.white,
    textColor: colors.text.primary,
    borderColor: colors.border.primary,
    placeholderColor: colors.text.tertiary,
  },
  error: {
    backgroundColor: colors.status.error.background,
    textColor: colors.status.error.text,
    borderColor: colors.status.error.text,
    placeholderColor: colors.text.tertiary,
  },
  success: {
    backgroundColor: colors.status.success.background,
    textColor: colors.status.success.text,
    borderColor: colors.status.success.text,
    placeholderColor: colors.text.tertiary,
  },
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  variant = 'default',
  size = 'sm',
  error = false,
  success = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  trailingButton,
  mask,
  maskChar = ' ',
  unmask = false,
  label,
  helperText,
  errorMessage,
  appearance,
  fullWidth = false,
  className,
  disabled,
  ...props
}, ref) => {
  // Função para aplicar máscara
  const applyMask = (value: string, maskPattern: string): string => {
    const cleanValue = value.replace(/\D/g, '') // Remove tudo que não é dígito
    let maskedValue = ''
    let valueIndex = 0
    
    for (let i = 0; i < maskPattern.length && valueIndex < cleanValue.length; i++) {
      if (maskPattern[i] === '0') {
        maskedValue += cleanValue[valueIndex]
        valueIndex++
      } else {
        maskedValue += maskPattern[i]
      }
    }
    
    return maskedValue
  }

  // Função para remover máscara
  const removeMask = (value: string): string => {
    return value.replace(/\D/g, '')
  }

  // Handler personalizado para onChange com máscara
  const handleMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mask) {
      props.onChange?.(e)
      return
    }

    const rawValue = e.target.value
    const maskedValue = applyMask(rawValue, mask)
    
    // Se unmask for true, envia o valor sem máscara para o form
    const finalValue = unmask ? removeMask(maskedValue) : maskedValue
    
    // Cria um novo event com o valor apropriado
    const maskedEvent = {
      ...e,
      target: {
        ...e.target,
        value: finalValue
      }
    }
    
    // Para mostrar o valor mascarado no input, mas enviar o valor limpo para o form
    if (unmask && e.target instanceof HTMLInputElement) {
      // Atualiza o valor visual do input
      e.target.value = maskedValue
    }
    
    props.onChange?.(maskedEvent as React.ChangeEvent<HTMLInputElement>)
  }

  // Determinar variante baseada nos estados
  const currentVariant = error ? 'error' : success ? 'success' : variant
  
  // Determinar aparência final
  const preset = variantPresets[currentVariant]
  const finalAppearance: InputAppearance = {
    backgroundColor: appearance?.backgroundColor || (disabled ? colors.palette.gray[50] : preset.backgroundColor),
    textColor: appearance?.textColor || (disabled ? colors.palette.gray[400] : preset.textColor),
    borderColor: appearance?.borderColor || (disabled ? colors.palette.gray[200] : preset.borderColor),
    placeholderColor: appearance?.placeholderColor || preset.placeholderColor,
  }

  // Classes de tamanho
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  }

  // Classes base do input
  const baseClasses = 'border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary'
  
  // Classes do container
  const containerClasses = cn(
    'relative',
    fullWidth ? 'w-full' : 'w-auto'
  )

  // Classes do input
  const inputClasses = cn(
    baseClasses,
    sizes[size],
    fullWidth && 'w-full',
    LeftIcon && 'pl-10',
    (RightIcon || trailingButton) && 'pr-10',
    disabled && 'cursor-not-allowed',
    className
  )

  // Estilos inline
  const inputStyles: React.CSSProperties = {
    backgroundColor: finalAppearance.backgroundColor,
    color: finalAppearance.textColor,
    borderColor: finalAppearance.borderColor,
  }

  // Classes dos ícones
  const iconClasses = 'absolute top-1/2 transform -translate-y-1/2 w-5 h-5'
  const leftIconClasses = cn(iconClasses, 'left-3')
  const rightIconClasses = cn(iconClasses, 'right-3')

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium" style={{ color: finalAppearance.textColor }}>
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className={containerClasses}>
        {/* Left Icon */}
        {LeftIcon && (
          <LeftIcon 
            className={leftIconClasses}
            style={{ color: finalAppearance.placeholderColor }}
          />
        )}

        {/* Input */}
        <input
          ref={ref}
          className={inputClasses}
          style={inputStyles}
          disabled={disabled}
          {...props}
          onChange={handleMaskedChange}
        />

        {/* Right Icon */}
        {RightIcon && !trailingButton && (
          <RightIcon 
            className={rightIconClasses}
            style={{ color: finalAppearance.placeholderColor }}
          />
        )}

        {/* Trailing Button */}
        {trailingButton && (
          <button
            type="button"
            onClick={trailingButton.onClick}
            aria-label={trailingButton['aria-label']}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors w-6 h-6 flex items-center justify-center rounded focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
            tabIndex={-1}
            disabled={disabled}
          >
            <trailingButton.icon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Helper Text / Error Message */}
      {(helperText || errorMessage) && (
        <div className="text-sm">
          {errorMessage ? (
            <span style={{ color: colors.status.error.text }}>
              {errorMessage}
            </span>
          ) : (
            <span style={{ color: colors.text.secondary }}>
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

Input.displayName = 'Input'
